/** Aplicar uma lente ao 3D, e desfazê-la.
 *
 *  Separado do motor de regras de propósito: o motor é puro e
 *  testa-se sem modelo nenhum; isto mexe no `three` e no Fragments e
 *  só se pode verificar no browser.
 *
 *  A regra de ouro aqui é a reversibilidade. Uma lente muda cores e
 *  visibilidade, que é exactamente o que o utilizador também mexe à
 *  mão. Se largar a lente e o modelo ficar com metade dos elementos
 *  escondidos sem se perceber porquê, isto faz mais mal do que bem.
 *  Por isso guarda-se o que foi tocado e desfaz-se tudo ao desligar.
 */

import * as paintEngine from '@viewer/lib/paintEngine'
import { viewerApi } from '@viewer/lib/viewerApi'

import type { ResultadoLente } from '@viewer/lens/lensRules'

/** A cor de quem não casou com regra nenhuma.
 *
 *  Cinzento apagado, não invisível: o objectivo é fazer sobressair o
 *  que as regras apanharam sem apagar o contexto do edifício. É a
 *  mesma ideia do "fantasma" que já existe na Aparência. */
export const COR_SEM_REGRA = '#6B7280'

interface ModeloDaLente {
  setVisible?: (ids: number[], visivel: boolean) => Promise<void>
  getItemsByVisibility?: (visivel: boolean) => Promise<number[]>
  threads?: {
    invoke: (modelId: string, method: string, args: unknown[]) => Promise<unknown>
  }
}

function modelo(modelId: string): ModeloDaLente | undefined {
  return viewerApi.getModel(modelId) as unknown as ModeloDaLente | undefined
}

/** O que foi tocado, para se poder desfazer. */
let escondidosPorModelo = new Map<string, number[]>()
let modelosPintados = new Set<string>()
let fantasmaPosto = false

/** O `paintEngine` indexa a textura pelo itemIndex interno do Fragments,
 *  não pelo localId (ver `classColors.repaintModel`). Escrever localIds
 *  directamente pintava outros elementos. Sem a tradução (modelo sem
 *  worker), fica o localId, como no `classColors`. */
async function paraIndices(
  modelId: string,
  pares: [number, string][],
): Promise<[number, string][]> {
  const invoke = modelo(modelId)?.threads?.invoke
  if (!invoke || pares.length === 0) return pares
  try {
    const indices = (await modelo(modelId)!.threads!.invoke(
      modelId,
      'getItemIdsByLocalIds',
      [pares.map(([id]) => id)],
    )) as number[]
    const out: [number, string][] = []
    pares.forEach(([, cor], i) => {
      const idx = indices?.[i]
      if (typeof idx === 'number' && idx >= 0) out.push([idx, cor])
    })
    return out
  } catch {
    return pares
  }
}

async function pintar(modelId: string, pares: [number, string][]): Promise<void> {
  paintEngine.setItemColors(modelId, await paraIndices(modelId, pares))
  modelosPintados.add(modelId)
}

/** A visibilidade mudou por fora do `viewerApi`: a contagem de
 *  escondidos ("Mostrar tudo"), a textura que apaga as arestas dos
 *  escondidos e o fantasma têm de saber. */
async function refrescarVisibilidade(): Promise<void> {
  try {
    await viewerApi._refreshVisibilityState?.()
  } catch {
    // Melhor esforço: o 3D já está certo, só os indicadores atrasam.
  }
  viewerApi._invalidate?.()
}

/** Aplica o resultado da lente.
 *
 *  Desfaz sempre a lente anterior primeiro: sem isso, trocar de lente
 *  ia acumulando elementos escondidos de lentes que já não estão
 *  activas, e ninguém conseguia perceber porquê. */
export async function aplicarLente(
  resultado: ResultadoLente,
  todasAsEntradas: readonly { modelId: string; localId: number }[],
  pintarSemRegra: boolean,
): Promise<void> {
  await limparLente()

  // Cor de fundo para quem não casou, escrita primeiro para que as
  // regras a possam cobrir a seguir.
  if (pintarSemRegra) {
    const porModelo = new Map<string, [number, string][]>()
    for (const e of todasAsEntradas) {
      const lista = porModelo.get(e.modelId) ?? []
      lista.push([e.localId, COR_SEM_REGRA])
      porModelo.set(e.modelId, lista)
    }
    for (const [modelId, pares] of porModelo) await pintar(modelId, pares)
  }

  for (const [modelId, porCor] of resultado.cores) {
    const pares: [number, string][] = []
    for (const [cor, ids] of porCor) {
      for (const id of ids) pares.push([id, cor])
    }
    await pintar(modelId, pares)
  }

  // Os translúcidos partilham a mesma cor apagada de quem não tem
  // regra: o motor de pintura escreve RGB, não alfa, por isso
  // "transparente" aqui quer dizer "apagado", e a ajuda diz isso.
  for (const [modelId, ids] of resultado.transparentes) {
    await pintar(
      modelId,
      ids.map((id) => [id, COR_SEM_REGRA] as [number, string]),
    )
  }

  // Só se escondem (e mais tarde só se voltam a mostrar) os que estavam
  // visíveis: o que o utilizador já tinha escondido à mão continua
  // escondido depois de a lente sair.
  for (const [modelId, ids] of resultado.escondidos) {
    const m = modelo(modelId)
    if (!m?.setVisible) continue
    try {
      let jaEscondidos = new Set<number>()
      if (m.getItemsByVisibility) {
        try {
          jaEscondidos = new Set(await m.getItemsByVisibility(false))
        } catch {
          jaEscondidos = new Set()
        }
      }
      const aEsconder = ids.filter((id) => !jaEscondidos.has(id))
      if (aEsconder.length === 0) continue
      await m.setVisible(aEsconder, false)
      escondidosPorModelo.set(modelId, aEsconder)
    } catch {
      // Um modelo que não deixe esconder não pode impedir os outros
      // de serem pintados.
    }
  }
  if (escondidosPorModelo.size > 0) await refrescarVisibilidade()
  else viewerApi._invalidate?.()

  // Fantasma: só as arestas, esbatidas, sem precisar de selecção. Vai
  // por último, porque calcula-se sobre o que ficou visível.
  if (resultado.fantasmas.size > 0) {
    const mapa = new Map<string, Set<number>>()
    for (const [modelId, ids] of resultado.fantasmas) mapa.set(modelId, new Set(ids))
    fantasmaPosto = true
    await viewerApi.setFantasmaDaLente?.(mapa)
  }
}

/** Devolve o modelo ao estado anterior à lente. */
export async function limparLente(): Promise<void> {
  const pintados = modelosPintados
  modelosPintados = new Set()
  for (const modelId of pintados) {
    // Recompor as camadas do `classColors` (paleta, Colorir por,
    // Aparência, Comparar IFC) em vez de pôr a textura a zero: a lente
    // escreve na mesma textura e apagá-la levava as outras cores.
    if (viewerApi.repaintOneModel && modelo(modelId)) {
      try {
        await viewerApi.repaintOneModel(modelId, { reset: true })
        continue
      } catch {
        // Sem recomposição, ao menos não fica a lente pintada.
      }
    }
    paintEngine.clearAllItemColors(modelId)
  }

  const escondidos = escondidosPorModelo
  escondidosPorModelo = new Map()
  for (const [modelId, ids] of escondidos) {
    const m = modelo(modelId)
    if (!m?.setVisible) continue
    try {
      await m.setVisible(ids, true)
    } catch {
      // Se falhar a mostrar, o botão "Mostrar tudo" do friso continua
      // a ser a saída de emergência.
    }
  }
  if (escondidos.size > 0) await refrescarVisibilidade()

  if (fantasmaPosto) {
    fantasmaPosto = false
    await viewerApi.setFantasmaDaLente?.(null)
  }
}

/** Só para os testes: diz o que está por desfazer. */
export function estadoInterno() {
  return {
    escondidos: escondidosPorModelo,
    pintados: modelosPintados,
  }
}
